import { Plus, Trash2 } from 'lucide-react'

import { newVariantId } from './migrate'
import type { PageVariant } from './types'

interface Props {
  variants?: PageVariant[]
  onChange: (v: PageVariant[]) => void
}

export function VariantsPanel({ variants, onChange }: Props) {
  const list = variants ?? []
  const set = (next: PageVariant[]) => onChange(next)

  const total = list.reduce((s, v) => s + (v.weight || 0), 0)

  const addVariant = () => {
    const v: PageVariant = {
      id: newVariantId(),
      name: `Variant ${String.fromCharCode(65 + list.length)}`,
      weight: 50,
      active: true,
    }
    set([...list, v])
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
        Define A/B variants. Traffic is split by weight (sticky per visitor).
        Variants without overrides render the base sections.
      </p>
      <button
        type="button"
        onClick={addVariant}
        style={{
          alignSelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 10px', fontSize: 13,
          border: '1px solid var(--color-border)', borderRadius: 4,
          background: 'var(--color-primary)', color: '#fff', cursor: 'pointer',
        }}
      >
        <Plus size={12} /> Add variant
      </button>
      {list.length > 0 && (
        <div style={{ fontSize: 13, color: total === 100 ? 'var(--color-text-secondary)' : '#d97706' }}>
          Total weight: {total}{total === 100 ? '' : ' (should sum to 100)'}
        </div>
      )}
      {list.map((v, idx) => (
        <div
          key={v.id}
          style={{
            border: '1px solid var(--color-border)', borderRadius: 6, padding: 10,
            display: 'grid', gap: 6, background: 'var(--color-bg-secondary)',
          }}
        >
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <input
              type="text"
              value={v.name}
              onChange={e => {
                const next = [...list]; next[idx] = { ...v, name: e.target.value }; set(next)
              }}
              style={{ ...inputStyle, fontWeight: 500, background: 'var(--color-bg)' }}
            />
            <button
              type="button"
              onClick={() => set(list.filter(x => x.id !== v.id))}
              title="Remove variant"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
            >
              <Trash2 size={14} />
            </button>
          </header>
          <Field label={`Weight: ${v.weight}%`}>
            <input
              type="range"
              min={0} max={100}
              value={v.weight}
              onChange={e => {
                const next = [...list]; next[idx] = { ...v, weight: Number(e.target.value) }; set(next)
              }}
            />
          </Field>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={v.active ?? true}
              onChange={e => {
                const next = [...list]; next[idx] = { ...v, active: e.target.checked }; set(next)
              }}
            />
            Active
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <Field label="Start (optional)">
              <input
                type="datetime-local"
                value={v.startedAt ?? ''}
                onChange={e => {
                  const next = [...list]; next[idx] = { ...v, startedAt: e.target.value || undefined }; set(next)
                }}
                style={inputStyle}
              />
            </Field>
            <Field label="End (optional)">
              <input
                type="datetime-local"
                value={v.endedAt ?? ''}
                onChange={e => {
                  const next = [...list]; next[idx] = { ...v, endedAt: e.target.value || undefined }; set(next)
                }}
                style={inputStyle}
              />
            </Field>
          </div>
        </div>
      ))}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
